import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { requestVipCheck } from "@/components/vip/VipAwardAnnouncer";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const DEFAULT_SPIN_COST = 25;

// Rose / amber / lime / sky / violet / emerald palette — cycles for any # of segments
const SEG_COLORS = [
  "#f43f5e",
  "#f59e0b",
  "#84cc16",
  "#38bdf8",
  "#a78bfa",
  "#10b981",
  "#ec4899",
  "#eab308",
];

/**
 * Prize wheel modal. Renders segments via SVG, animates rotation to land on the
 * winning slice returned by the backend, then celebrates.
 */
export default function LiveWheelModal({ open, onOpenChange, streamId, segments = [], wheel = null, onWheelChanged }) {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const spinKeyRef = useRef(null); // one key per confirmed attempt — retries never charge twice
  const wheelRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setResult(null);
      setBusy(false);
      setConfirming(false);
      spinKeyRef.current = null;
    }
  }, [open]);

  // Creator-saved wheel (name / options / price); falls back to legacy segments prop.
  const cleaned = (wheel?.segments?.length ? wheel.segments : segments).filter((s) => (s || "").trim());
  const n = cleaned.length;
  const SPIN_COST = Number(wheel?.price) || DEFAULT_SPIN_COST;
  const wheelName = wheel?.name || "Prize wheel";
  const balance = user?.coins || 0;
  const shortfall = Math.max(0, SPIN_COST - balance);
  const enabled = wheel ? wheel.enabled !== false : n >= 2;

  const spin = async () => {
    if (busy || n < 2 || !enabled) return;
    if (balance < SPIN_COST) {
      toast.error(`Not enough coins: a spin costs ${SPIN_COST.toLocaleString()} and you have ${balance.toLocaleString()}.`);
      return;
    }
    if (!confirming) { setConfirming(true); return; } // first tap = review, second tap = pay
    setBusy(true);
    setResult(null);
    try {
      if (!spinKeyRef.current) spinKeyRef.current = (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`);
      const { data } = await api.post(`/live/${streamId}/wheel/spin`, {
        spin_key: spinKeyRef.current,
        confirmed_price: SPIN_COST,
        confirmed_segments: cleaned,
      });
      spinKeyRef.current = null;
      requestVipCheck();
      setConfirming(false);
      // Compute target rotation: land the pointer (top, 12 o'clock) on the winning
      // slice's mid-angle. Segments are drawn clockwise starting at -90deg (top).
      const segAngle = 360 / n;
      const winMid = data.winning_index * segAngle + segAngle / 2;
      // Add 6 full spins on top so it feels satisfying
      const target = 360 * 6 + (360 - winMid);
      // Start from where the wheel is currently (mod 360) for smoothness
      const from = rotation % 360;
      setRotation(from + (target - from));
      setTimeout(() => {
        setResult(data);
        setBusy(false);
        if (refresh) refresh();
      }, 4200);
    } catch (e) {
      const status = e?.response?.status;
      toast.error(e?.response?.data?.detail || "Spin failed — nothing was charged. Tap Spin to try again.");
      if (status === 409 || status === 400) { spinKeyRef.current = null; setConfirming(false); onWheelChanged?.(); }
      // network failure: key kept so a retry returns the same spin instead of charging twice
      setBusy(false);
    }
  };

  if (!open) return null;

  const size = 300;
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 6;

  // Build SVG path for each pie slice
  const buildSlicePath = (index) => {
    if (n === 1) {
      // full circle if only one segment (shouldn't happen — backend requires >=2)
      return `M ${cx - r} ${cy} A ${r} ${r} 0 1 1 ${cx + r} ${cy} A ${r} ${r} 0 1 1 ${cx - r} ${cy} Z`;
    }
    const start = -Math.PI / 2 + (index * 2 * Math.PI) / n;
    const end = -Math.PI / 2 + ((index + 1) * 2 * Math.PI) / n;
    const x1 = cx + r * Math.cos(start);
    const y1 = cy + r * Math.sin(start);
    const x2 = cx + r * Math.cos(end);
    const y2 = cy + r * Math.sin(end);
    const largeArc = end - start > Math.PI ? 1 : 0;
    return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
  };

  const labelPos = (index) => {
    const mid = -Math.PI / 2 + ((index + 0.5) * 2 * Math.PI) / n;
    const lr = r * 0.62;
    return {
      x: cx + lr * Math.cos(mid),
      y: cy + lr * Math.sin(mid),
      angleDeg: (mid * 180) / Math.PI + 90,
    };
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      data-testid="live-wheel-modal"
    >
      <div className="relative w-full max-w-md bg-zinc-950 border border-white/10 rounded-3xl p-6 pt-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-display text-2xl text-white flex items-center gap-2">
              <Sparkles size={20} className="text-yellow-300" /> {wheelName}
            </div>
            <div className="text-xs text-white/60 mt-0.5" data-testid="live-wheel-price">
              {SPIN_COST.toLocaleString()} coins per spin · {n} segments
            </div>
          </div>
          <button
            data-testid="live-wheel-close"
            onClick={() => onOpenChange(false)}
            className="text-white/60 hover:text-white"
          >
            <X size={22} />
          </button>
        </div>

        <div className="relative flex items-center justify-center py-4">
          {/* pointer */}
          <div className="absolute top-1 left-1/2 -translate-x-1/2 z-10 w-0 h-0 border-l-[12px] border-l-transparent border-r-[12px] border-r-transparent border-t-[18px] border-t-yellow-300 drop-shadow" />
          <svg
            ref={wheelRef}
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            data-testid="live-wheel-svg"
            style={{
              transform: `rotate(${rotation}deg)`,
              transition: "transform 4s cubic-bezier(0.16, 1, 0.3, 1)",
              filter: "drop-shadow(0 10px 30px rgba(0,0,0,0.5))",
            }}
          >
            <circle cx={cx} cy={cy} r={r + 3} fill="#111" />
            {cleaned.map((seg, i) => {
              const color = SEG_COLORS[i % SEG_COLORS.length];
              const { x, y, angleDeg } = labelPos(i);
              return (
                <g key={i}>
                  <path d={buildSlicePath(i)} fill={color} stroke="#0a0a0a" strokeWidth="2" />
                  <text
                    x={x}
                    y={y}
                    fill="white"
                    fontSize="12"
                    fontWeight="700"
                    textAnchor="middle"
                    alignmentBaseline="middle"
                    transform={`rotate(${angleDeg} ${x} ${y})`}
                  >
                    {seg.length > 14 ? seg.slice(0, 13) + "…" : seg}
                  </text>
                </g>
              );
            })}
            <circle cx={cx} cy={cy} r={16} fill="#fbbf24" stroke="#0a0a0a" strokeWidth="3" />
          </svg>
        </div>

        {result ? (
          <div
            className="text-center mb-4 bg-yellow-400/10 border border-yellow-400/40 rounded-2xl py-4 px-3"
            data-testid="live-wheel-result"
          >
            <div className="text-xs uppercase tracking-widest text-yellow-300">You won</div>
            <div className="font-display text-2xl text-white mt-1">{result.winning_segment}</div>
            <div className="text-xs text-white/60 mt-1">
              {result.coins_left.toLocaleString()} coins remaining
            </div>
          </div>
        ) : (
          <div className="text-center text-sm text-white/60 mb-4">
            Your balance: <span className="text-yellow-300 font-semibold">{balance.toLocaleString()}</span>
            {!enabled && <div className="text-xs text-rose-300 mt-1" data-testid="live-wheel-off">The creator has turned the wheel off.</div>}
            {enabled && shortfall > 0 && (
              <div className="text-xs text-rose-300 mt-1" data-testid="live-wheel-insufficient">
                You can't spin yet: a spin costs {SPIN_COST.toLocaleString()} coins and you have {balance.toLocaleString()}. Top up {shortfall.toLocaleString()} more.
              </div>
            )}
            {enabled && shortfall === 0 && confirming && (
              <div className="text-xs text-yellow-200 mt-1" data-testid="live-wheel-confirm-note">
                Confirm to pay {SPIN_COST.toLocaleString()} coins for one spin of {wheelName}.{" "}
                <button type="button" data-testid="live-wheel-cancel" onClick={() => setConfirming(false)} className="underline text-white/70">Cancel</button>
              </div>
            )}
          </div>
        )}

        <button
          data-testid="live-wheel-spin"
          onClick={spin}
          disabled={busy || n < 2 || !enabled || balance < SPIN_COST}
          className="w-full bg-yellow-400 hover:bg-yellow-300 disabled:opacity-60 disabled:cursor-not-allowed text-black font-bold py-3.5 rounded-full flex items-center justify-center gap-2 transition"
        >
          {busy ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Spinning…
            </>
          ) : (
            <>{confirming ? `Confirm spin · pay ${SPIN_COST.toLocaleString()} coins` : `Spin · ${SPIN_COST.toLocaleString()} coins`}</>
          )}
        </button>
      </div>
    </div>,
    document.body
  );
}
